import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader";
import { Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import type { CalculationType } from "../../../../../../../../../api/types/fieldConfigurationTypes";
import {
  selectAggregationFields,
  selectFieldsForCalculation,
} from "../../../../../store/selectors/fieldsStateSelectors";
import { selectCustomFormulaMeta } from "../../../../../store/selectors/metaDataSelectors";
import ReferenceCalculatedFieldSelect from "./controls/ReferenceCalculatedFIeldSelect";
import TypeOfPredefinedCalculationSelect from "./controls/TypeOfPredefinedCalculationSelect";
import type { CustomColumnData } from "./customColumn.types";
import useSetupCustomCalculationMenu from "./hooks/useSetupCustomCalculationMenu";

interface Props {
  calculationType: CalculationType;
  fieldGuid: string | undefined; //undefined for a new custom field
  onClose: () => void;
  onApply: (data: CustomColumnData) => void;
  readonly: boolean;
}

const NewCustomColumnCalculationSetup = ({ calculationType, fieldGuid, onClose, onApply, readonly }: Props) => {
  const fieldsForCalculations = useSelector(selectFieldsForCalculation);
  const aggregationFields = useSelector(selectAggregationFields);
  const customFormulas = useSelector(selectCustomFormulaMeta);

  const {
    isDirty,
    isValid,
    nameError,
    selectedFormulaParameterIdsError,
    selectedCalculationType,
    selectedFormulaParameterIds,
    name,
    onNameChange,
    onSelectedFormulaParameterIdsChange,
    onSelectedCalculationTypeChange,
  } = useSetupCustomCalculationMenu(calculationType, fieldGuid, aggregationFields);

  const handleApply = () => {
    const customFormulaMeta = customFormulas?.find((meta) => meta.calculationType === selectedCalculationType) || {
      formula: "",
      format: undefined,
      calculationType: selectedCalculationType,
    };
    onApply({
      name,
      selectedFormulaParameterIds,
      selectedCalculationType,
      formula: customFormulaMeta.formula,
      format: customFormulaMeta.format,
    });
  };

  return (
    <Stack>
      <DialogHeader text="New Calculation" onClose={onClose} />
      <Divider />
      {fieldsForCalculations.length > 0 && (
        <Stack spacing={2} sx={{ p: 2.5 }}>
          <TypeOfPredefinedCalculationSelect
            selectedCalculationType={selectedCalculationType}
            onSelectedCalculationTypeChange={onSelectedCalculationTypeChange}
            readonly
          />
          <ReferenceCalculatedFieldSelect
            selectedFieldGuid={selectedFormulaParameterIds[0] ?? ""}
            onSelectedFieldGuidChange={(fieldGuid) => onSelectedFormulaParameterIdsChange([fieldGuid])}
            calculatedFields={fieldsForCalculations}
            selectedFieldGuidError={selectedFormulaParameterIdsError}
            readonly={readonly}
          />
          <TextField
            autoFocus={!isDirty && isValid}
            label="Name"
            value={name}
            onChange={(evt) => onNameChange(evt.target.value)}
            error={!!nameError}
            helperText={nameError}
          />
        </Stack>
      )}
      {fieldsForCalculations.length === 0 && (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", py: 2 }}>
          No calculated fields found
        </Typography>
      )}
      <Divider />
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", px: 2, py: 1.5 }}>
        <Button variant="text" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={!isValid}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};

export default NewCustomColumnCalculationSetup;
